import { getSessionToken } from "../helpers/getSessionToken";
import { forceCloseModal } from "../helpers/forceCloseModal";
import { isChatDebugEnabled, chatDebugLog } from "../helpers/chatDebug";
import { syncRoleplaySettingsToggleState } from "./chatSettings";
import { handleAjaxError } from "../actions/handleAjaxError";
import { renderMessage } from "../actions/renderMessage";
import { appendUserMessage } from "../actions/appendUserMessage";
import { submitChatMessages } from "../actions/submitChatMessages";
import { updateCredits } from "../actions/updateCredits";
import { scrollToChatBottom } from "../actions/scrollToChatBottom";
import { getLoaderDelayMs } from "../helpers/frontendDelays";
import { ACCESS_REQUIREMENT_CODES } from "../helpers/accessRequirementErrors";
import {
    initMessageActions,
    invalidateChatContentRequests,
    syncChatContentRequestContext,
} from "./chatContent";
import { replaceConversationState } from "./conversationState";
import {
    appendGuestLocalHistory,
    clearGuestLocalHistory,
    restoreGuestLocalHistory
} from "../helpers/guestLocalHistory";

const ROLEPLAY_STORY_STARTED_STORAGE_PREFIX = 'growtype_roleplay_story_started_';
const ROLEPLAY_STOP_CONFIRMATION_TEXT = "Roleplay stopped. You're back in normal chat now.";
const ROLEPLAY_REQUEST_STATES = {
    IDLE: 'idle',
    STARTING: 'starting',
    STOPPING: 'stopping',
};

export function normalizeRoleplayRequestState(requestState = '') {
    return Object.values(ROLEPLAY_REQUEST_STATES).includes(requestState)
        ? requestState
        : ROLEPLAY_REQUEST_STATES.IDLE;
}

export function getRoleplayRequestState($chatForm = null) {
    const $targetChatForm = $chatForm && $chatForm.length ? $chatForm : $('.g-chatform').first();
    return normalizeRoleplayRequestState(
        ($targetChatForm.attr('data-roleplay-request-state') || '').trim()
    );
}

export function getRoleplayStoryStartedStorageKey(sessionToken) {
    return sessionToken ? ROLEPLAY_STORY_STARTED_STORAGE_PREFIX + sessionToken : '';
}

export function markRoleplayStoryStarted(sessionToken) {
    const storageKey = getRoleplayStoryStartedStorageKey(sessionToken);
    if (!storageKey) return;
    try {
        window.localStorage.setItem(storageKey, '1');
    } catch (error) {
        console.warn('[roleplay-story-start] failed to persist started flag', error);
    }
}

export function clearRoleplayStoryStarted(sessionToken) {
    const storageKey = getRoleplayStoryStartedStorageKey(sessionToken);
    if (!storageKey) return;
    try {
        window.localStorage.removeItem(storageKey);
    } catch (error) {
        console.warn('[roleplay-story-start] failed to clear started flag', error);
    }
}

export function hasRoleplayStoryStarted(sessionToken) {
    const storageKey = getRoleplayStoryStartedStorageKey(sessionToken);
    if (!storageKey) return false;
    try {
        return window.localStorage.getItem(storageKey) === '1';
    } catch (error) {
        console.warn('[roleplay-story-start] failed to read started flag', error);
        return false;
    }
}

export function clearIntroQuizCookie() {
    const expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = `growtype_intro_quiz=; expires=${expires}; path=/`;
    document.cookie = `growtype_intro_quiz=; expires=${expires}; path=/; SameSite=Lax`;
}

export function pruneRoleplayQuizMessages($contentWrapper) {
    if (!$contentWrapper || !$contentWrapper.length) return;
    const $summaryMessages = $contentWrapper.find('.g-chatform-message:has([data-roleplay-quiz-summary="true"])');
    if (!$summaryMessages.length) return;
    $summaryMessages.each(function () {
        $(this).remove();
    });
}

export function maybePruneStartedRoleplayQuiz($chatForm = null) {
    const $targetChatForm = $chatForm && $chatForm.length ? $chatForm : $('.g-chatform').first();
    if (!$targetChatForm.length) return;
    const sessionToken = getSessionToken($targetChatForm);
    if (!hasRoleplayStoryStarted(sessionToken)) return;
    pruneRoleplayQuizMessages($targetChatForm.find('.g-chatform-content'));
}

export function cleanRoleplayStartUrl(rawUrl = window.location.href) {
    const url = new URL(rawUrl, window.location.origin);
    url.searchParams.delete('intro_quiz');
    url.searchParams.delete('roleplay_quiz_slug');
    return url.toString();
}

function resolveRenderableBackgroundValue(backgroundValue = '') {
    const normalizedValue = String(backgroundValue || '').trim();

    if (!normalizedValue || normalizedValue === 'none') {
        return normalizedValue;
    }

    const lowerValue = normalizedValue.toLowerCase();
    const isDirectRenderableValue =
        /^https?:\/\//.test(normalizedValue) ||
        normalizedValue.startsWith('/') ||
        normalizedValue.includes('gradient(') ||
        normalizedValue.includes('rgb(') ||
        lowerValue.endsWith('.mp4') ||
        lowerValue.endsWith('.webm') ||
        lowerValue.includes('.mp4?') ||
        lowerValue.includes('.webm?') ||
        normalizedValue.startsWith('#');

    if (isDirectRenderableValue) {
        return normalizedValue;
    }

    const option = document.querySelector(
        `select[name="chat_background_value"] option[value="${CSS.escape(normalizedValue)}"]`
    );

    if (!option) {
        return normalizedValue;
    }

    return (
        option.getAttribute('data-preview-image') ||
        option.getAttribute('data-main-image') ||
        option.getAttribute('data-bg-image') ||
        normalizedValue
    ).trim();
}

export function buildNormalChatUrl(rawUrl = window.location.href) {
    const url = new URL(rawUrl, window.location.origin);
    url.searchParams.delete('intro_quiz');
    url.searchParams.delete('roleplay_quiz_slug');
    url.searchParams.delete('roleplay_scenario');
    url.searchParams.delete('roleplay_difficulty');
    return url.toString();
}

function isUrlOwnedRoleplaySession() {
    let scenario = '';
    try {
        scenario = new URL(window.location.href, window.location.origin)
            .searchParams.get('roleplay_scenario') || '';
        const owned = !!scenario && scenario !== 'none';
        if (isChatDebugEnabled('roleplayHeader')) {
            chatDebugLog('roleplayHeader', '[roleplay-header-debug] isUrlOwnedRoleplaySession', {
                locationHref: window.location.href,
                roleplayScenario: scenario,
                isUrlOwned: owned,
            });
        }
        return owned;
    } catch (error) {
        if (isChatDebugEnabled('roleplayHeader')) {
            chatDebugLog('roleplayHeader', '[roleplay-header-debug] isUrlOwnedRoleplaySession:error', {
                locationHref: window.location.href,
                roleplayScenario: scenario,
                error: String(error),
            });
        }
        return false;
    }
}

export function resolveRoleplayBrowserUrl(rawUrl = window.location.href, options = {}) {
    const cleanedUrl = cleanRoleplayStartUrl(rawUrl);
    const isActive = options.isActive === true;
    const roleplaySessionMode = String(options.roleplay_session_mode || '').trim();
    const shouldKeepRoleplayUrl = isActive && (
        roleplaySessionMode === 'url' ||
        (roleplaySessionMode === '' && isUrlOwnedRoleplaySession())
    );

    return shouldKeepRoleplayUrl
        ? cleanedUrl
        : buildNormalChatUrl(cleanedUrl);
}

export function getChatRenderArgs($chatForm) {
    const storedArgs = $chatForm.attr('data-chat-render-args');
    if (storedArgs) {
        try {
            return JSON.parse(storedArgs);
        } catch (error) {
            console.error('[roleplay-story-start] failed to parse stored render args', error);
        }
    }

    const rawArgs = $chatForm.find('.g-chatform-content-render').first().attr('data-args');
    if (rawArgs) {
        $chatForm.attr('data-chat-render-args', rawArgs);
        try {
            return JSON.parse(rawArgs);
        } catch (error) {
            console.error('[roleplay-story-start] failed to parse render args', error);
        }
    }

    const currentUrl = new URL(window.location.href, window.location.origin);
    const pathnameParts = currentUrl.pathname.split('/').filter(Boolean);
    const characterSlug = pathnameParts[pathnameParts.length - 1] || '';
    const roleplayScenario = currentUrl.searchParams.get('roleplay_scenario') || '';
    const gcwm = currentUrl.searchParams.get('gcwm') || '';
    const chatId = $chatForm.attr('id') || '';

    return {
        character_slug: characterSlug,
        roleplay_scenario: roleplayScenario,
        user_token: $chatForm.attr('data-user-token') || '',
        session_token: $chatForm.attr('data-session-token') || '',
        chat_height: '800px',
        chat_id: chatId,
        response_min_time: parseInt($chatForm.attr('data-response-min-time') || '2', 10),
        disable_one_by_one_loading_animation: Boolean(
            window.growtype_chat_disable_one_by_one_loading_animation ??
            window.growtypeChatDisableOneByOneLoadingAnimation ??
            window.growtypeChatSettings?.disableOneByOneLoadingAnimation
        ),
        gcwm: gcwm,
        intro_quiz: '',
        roleplay_quiz_slug: '',
        bot_profile: window.growtype_chat?.[chatId]?.bot_profile || '',
    };
}

function buildLightweightChatRenderArgs(renderArgs = {}) {
    const requestArgs = { ...renderArgs };

    delete requestArgs.available_backgrounds;
    delete requestArgs.bot_profile;
    requestArgs.omit_client_state_payload = true;

    return requestArgs;
}

export function appendNormalChatHandoffMessage($chatForm, responsePayload) {
    const fallbackHtml = `
      <div class="g-chatform-message" data-role="admin" data-message-type="text" data-roleplay-stop-confirmation="true">
        <div class="g-chatform-message-inner">
          <div class="g-chatform-message-inner-top">
            <div class="g-chatform-message-content">
              <div class="g-chatform-message-content-inner">
                <p>${ROLEPLAY_STOP_CONFIRMATION_TEXT}</p>
              </div>
            </div>
          </div>
          <div class="g-chatform-message-inner-bottom">
            <div class="g-chatform-message-time"></div>
          </div>
        </div>
      </div>
    `;
    const fallbackPayload = {
        response_messages: [{
            html: fallbackHtml,
            delay: 0,
        }],
        local_history_append: [{
            text: ROLEPLAY_STOP_CONFIRMATION_TEXT,
            category: 'text',
            isUser: false,
            createdAt: Date.now(),
        }],
    };
    const payload = responsePayload && typeof responsePayload === 'object'
        ? responsePayload
        : fallbackPayload;
    const responseMessages = Array.isArray(payload?.response_messages) ? payload.response_messages : [];
    const localHistoryAppend = Array.isArray(payload?.local_history_append) ? payload.local_history_append : [];
    const hasFallbackMessageAlready = $chatForm
        .find('.g-chatform-content .g-chatform-message[data-roleplay-stop-confirmation="true"]')
        .length > 0;

    if (responseMessages.length === 0) {
        return Promise.resolve(false);
    }

    if (!responsePayload && hasFallbackMessageAlready) {
        return Promise.resolve(false);
    }

    if (localHistoryAppend.length > 0) {
        appendGuestLocalHistory($chatForm, localHistoryAppend.map(function (entry) {
            return {
                ...entry,
                createdAt: entry?.createdAt || Date.now(),
            };
        }));
    }

    return renderMessage($chatForm, {
        success: true,
        data: {
            response_messages: responseMessages,
            automatic_voice_messages: false,
            redirect_url: '',
        },
    }, null).then(function () {
        return true;
    });
}

export function switchRoleplayConversationSession($chatForm, options = {}) {
    const previousSessionToken = String(getSessionToken($chatForm) || '').trim();
    const nextSessionToken = String(options.sessionToken || '').trim();
    const nextRoleplayScenario = String(options.roleplayScenario || '').trim();
    const activatedBackgroundValue = String(options.activatedBackgroundValue || '').trim();
    const isActive = options.isActive === true;
    const roleplayKey = String(options.roleplayKey || '').trim();
    const onRendered = typeof options.onRendered === 'function' ? options.onRendered : null;
    const onError = typeof options.onError === 'function' ? options.onError : null;
    const traceId = String(options.traceId || '').trim();
    const renderResponse = options.renderResponse || null;

    chatDebugLog('roleplayStep', '[roleplay-trace] switch-session:start', {
        traceId,
        previousSessionToken,
        nextSessionToken,
        nextRoleplayScenario,
        isActive,
        formSessionToken: $chatForm.attr('data-session-token') || '',
    });

    if (!nextSessionToken || (isActive && nextSessionToken.startsWith('PENDING:'))) {
        chatDebugLog('roleplayStep', '[roleplay-trace] switch-session:rejected', {
            traceId,
            nextSessionToken,
            isActive,
        });
        if (onError) {
            onError({
                message: !nextSessionToken
                    ? 'Missing next session token.'
                    : 'Roleplay session was not materialized.',
                session_token: nextSessionToken,
            });
        }
        return;
    }

    if (isActive) {
        $chatForm.attr('data-normal-session-token', previousSessionToken || $chatForm.attr('data-normal-session-token') || '');
    } else {
        $chatForm.attr('data-normal-session-token', nextSessionToken);
    }

    updateSessionTokenReferences($chatForm, nextSessionToken);
    $chatForm.attr('data-roleplay-key', isActive ? roleplayKey : '');
    syncChatContentRequestContext($chatForm, {
        ...getChatRenderArgs($chatForm),
        session_token: nextSessionToken,
        roleplay_scenario: isActive ? nextRoleplayScenario : '',
        roleplay_trace_id: traceId,
    });
    invalidateChatContentRequests($chatForm, {
        reason: isActive ? 'roleplay-start' : 'roleplay-stop',
    });
    chatDebugLog('roleplayStep', '[roleplay-trace] switch-session:references-updated', {
        traceId,
        requestedSessionToken: nextSessionToken,
        formSessionToken: $chatForm.attr('data-session-token') || '',
        shellSessionToken: window.growtype_chat?.['gc-shell-chat']?.session_token || '',
    });

    const handleRendered = function (response, $refreshedChatForm) {
        if (onRendered) {
            onRendered(response, $refreshedChatForm);
        }
    };
    const handleRenderError = function (refreshError) {
        if (onError) {
            onError(refreshError);
        }
    };
    const renderOptions = {
        animateFreshMessages: typeof options.animateFreshMessages === 'boolean'
            ? options.animateFreshMessages
            : isActive,
        restoreGuestHistory: true,
        clearGuestHistoryFirst: false,
        keepRoleplayHeaderWrapper: true,
        restoredRoleplayHeaderActive: isActive,
        activatedBackgroundValue,
        scrollToBottom: true,
    };

    if (renderResponse?.success === true && renderResponse?.data?.render) {
        chatDebugLog('roleplayStep', '[roleplay-trace] switch-session:using-transition-response-render', {
            traceId,
            sessionToken: nextSessionToken,
            isActive,
        });
        replaceConversationState($chatForm, renderResponse, {
            animate: renderOptions.animateFreshMessages,
            scrollToBottom: renderOptions.scrollToBottom,
        }).then(function () {
            handleRendered(renderResponse, $chatForm);
        }).catch(handleRenderError);
        return;
    }

    handleRenderError({
        message: 'Session transition response did not include conversation state.',
        roleplay_trace_id: traceId,
    });
}

export function showRoleplayTransitionLoader($chatForm) {
    clearRoleplayTransitionLoader($chatForm);

    $chatForm
        .find('.g-chatform-content .g-chatform-message:visible')
        .attr('data-roleplay-transition-hidden', 'true')
        .stop(true, true)
        .fadeOut(180);

    const $loaderMessage = appendUserMessage($chatForm, 'text');
    $loaderMessage
        .attr('data-roleplay-transition-loader', 'true')
        .delay(180)
        .fadeIn(180);

    return {
        startedAt: Date.now(),
        $message: $loaderMessage,
        minimumDuration: getLoaderDelayMs($chatForm, 'roleplay_toggle', 'text'),
    };
}

export function clearRoleplayTransitionLoader($chatForm) {
    $chatForm
        .find('.g-chatform-message[data-roleplay-transition-loader="true"]')
        .remove();
}

export function restoreRoleplayTransitionMessages($chatForm) {
    clearRoleplayTransitionLoader($chatForm);
    $chatForm
        .find('.g-chatform-message[data-roleplay-transition-hidden="true"]')
        .removeAttr('data-roleplay-transition-hidden')
        .stop(true, true)
        .fadeIn(180);
}

export function completeRoleplayTransition(transition, callback) {
    const elapsed = Date.now() - (transition?.startedAt || Date.now());
    const remainingDelay = Math.max(0, (transition?.minimumDuration || 0) - elapsed);
    window.setTimeout(callback, remainingDelay);
}

export function prepareRoleplayStoryRefreshRender(renderHtml, options = {}) {
    if (typeof renderHtml !== 'string' || renderHtml.trim() === '') {
        return renderHtml;
    }

    const shouldAnimateFreshMessages = options.animateFreshMessages === true;
    if (!shouldAnimateFreshMessages) {
        return renderHtml;
    }

    const $renderWrapper = $('<div>').html(renderHtml);
    const $scope = $renderWrapper.find('.g-chatform').first().length
        ? $renderWrapper.find('.g-chatform').first()
        : $renderWrapper;

    $scope
        .find('.g-chatform-content .g-chatform-message')
        .filter(function () {
            const inlineStyle = String($(this).attr('style') || '')
                .replace(/\s+/g, '')
                .toLowerCase();
            return !inlineStyle.includes('display:none');
        })
        .attr('data-roleplay-refresh-reveal', 'true')
        .css('display', 'none');

    const preparedChatForm = $renderWrapper.find('.g-chatform').first();

    return preparedChatForm.length
        ? preparedChatForm.prop('outerHTML') || renderHtml
        : $renderWrapper.html();
}

export function revealRoleplayStoryRefreshedMessages($chatForm, options = {}) {
    const shouldAnimateFreshMessages = options.animateFreshMessages === true;

    if (!shouldAnimateFreshMessages || !$chatForm?.length) {
        if (options.scrollToBottom === true || $chatForm?.length) {
            scrollToChatBottom(0, true);
            window.requestAnimationFrame(function () {
                scrollToChatBottom(0, true);
            });
            setTimeout(function () {
                scrollToChatBottom(0, true);
            }, 100);
        }
        return Promise.resolve(false);
    }

    const $messages = $chatForm
        .find('.g-chatform-content .g-chatform-message[data-roleplay-refresh-reveal="true"]');

    if (!$messages.length) {
        return Promise.resolve(false);
    }

    let revealChain = Promise.resolve();

    $messages.each(function (index, element) {
        const $message = $(element);
        const $actions = $message.find('.g-chatform-message-actions');

        $actions.hide();

        revealChain = revealChain.then(function () {
            return new Promise(function (resolve) {
                $message.stop(true, true).fadeIn(index === 0 ? 220 : 260, function () {
                    $message.removeAttr('data-roleplay-refresh-reveal');
                    $actions.fadeIn(180);
                    scrollToChatBottom(200, true);
                    resolve();
                });
            });
        });
    });

    return revealChain.then(function () {
        return true;
    });
}

export function getPersistentRoleplayHeaderStorageKey($chatForm = null) {
    const renderArgs = getChatRenderArgs($chatForm);
    const characterSlug = String(renderArgs?.character_slug || '').trim();
    const pathSlug = window.location.pathname.replace(/^\/+|\/+$/g, '') || 'chat';
    const storageSlug = characterSlug !== '' ? characterSlug : pathSlug;

    return `growtype_roleplay_header_wrapper:${storageSlug}`;
}

export function persistRoleplayHeaderWrapper($chatForm, $wrapper) {
    if (!$chatForm?.length || !$wrapper?.length) {
        return;
    }
    if (isChatDebugEnabled('roleplayHeader')) {
        chatDebugLog('roleplayHeader', '[roleplay-header-debug] persist:enter', {
            isUrlOwned: isUrlOwnedRoleplaySession(),
            storageKey: getPersistentRoleplayHeaderStorageKey($chatForm),
            wrapperRoleplayKey: ($wrapper.attr('data-roleplay-key') || '').trim(),
        });
    }
    logRoleplayStep('[roleplay-header] persist:start', {
        storageKey: getPersistentRoleplayHeaderStorageKey($chatForm),
        wrapperRoleplayKey: ($wrapper.attr('data-roleplay-key') || '').trim(),
    }, $wrapper.find('[data-roleplay-toggle="true"], [data-roleplay-settings-toggle="true"]').first(), $chatForm);
    try {
        window.localStorage.setItem(
            getPersistentRoleplayHeaderStorageKey($chatForm),
            $wrapper.prop('outerHTML') || ''
        );
        if (isChatDebugEnabled('roleplayHeader')) {
            chatDebugLog('roleplayHeader', '[roleplay-header-debug] persist:written', {
                storageKey: getPersistentRoleplayHeaderStorageKey($chatForm),
            });
        }
        logRoleplayStep('[roleplay-header] persist:complete', {
            storageKey: getPersistentRoleplayHeaderStorageKey($chatForm),
        }, $wrapper.find('[data-roleplay-toggle="true"], [data-roleplay-settings-toggle="true"]').first(), $chatForm);
    } catch (error) {
        console.warn('[roleplay-header] failed to persist wrapper', error);
    }
}

export function loadPersistentRoleplayHeaderWrapper($chatForm) {
    if (!$chatForm?.length) return null;
    try {
        const storageKey = getPersistentRoleplayHeaderStorageKey($chatForm);
        const storedHtml = window.localStorage.getItem(storageKey);
        if (window.growtypeChatDebug) {
            chatDebugLog('roleplayHeader', '[roleplay-header-debug] load', {
                storageKey,
                hasStored: !!storedHtml,
                storedLength: storedHtml ? storedHtml.length : 0,
            });
        }
        if (!storedHtml || storedHtml.trim() === '') return null;
        const $wrapper = $(storedHtml).first();
        return $wrapper.length ? $wrapper : null;
    } catch (error) {
        console.warn('[roleplay-header] failed to load persisted wrapper', error);
        return null;
    }
}

export function clearPersistentRoleplayHeaderWrapper($chatForm = null) {
    try {
        window.localStorage.removeItem(getPersistentRoleplayHeaderStorageKey($chatForm));
    } catch (error) {
        console.warn('[roleplay-header] failed to clear persisted wrapper', error);
    }
}

export function clonePersistentRoleplayHeaderWrapper($chatForm) {
    if (!$chatForm?.length) return null;
    const $wrapper = getCanonicalRoleplayHeaderWrapper(
        $chatForm.find('.g-chatform-header').first()
    );
    if (!$wrapper.length) return null;
    return $wrapper.clone();
}

export function getCanonicalRoleplayHeaderWrapper($header) {
    const $wrappers = $header.find('.roleplay-details-wrapper');
    if ($wrappers.length < 2) return $wrappers;
    const $canonicalWrapper = $wrappers.last();
    $wrappers.not($canonicalWrapper).remove();
    return $canonicalWrapper;
}

export function syncRoleplayHeaderWrapperInPlace($targetWrapper, $sourceWrapper) {
    if (!$targetWrapper?.length || !$sourceWrapper?.length) return $targetWrapper;

    const sourceAttributes = new Map();

    Array.from($sourceWrapper.get(0)?.attributes || []).forEach((attribute) => {
        sourceAttributes.set(attribute.name, attribute.value);
    });

    Array.from($targetWrapper.get(0)?.attributes || []).forEach((attribute) => {
        if (!sourceAttributes.has(attribute.name)) {
            $targetWrapper.removeAttr(attribute.name);
        }
    });

    sourceAttributes.forEach((value, name) => {
        $targetWrapper.attr(name, value);
    });

    $targetWrapper.html($sourceWrapper.html());

    return $targetWrapper;
}

export function placeRoleplayHeaderWrapper($header, $wrapper) {
    if (!$header?.length || !$wrapper?.length) return $wrapper;

    const $users = $header.children('.g-chatform-header-users').first();
    const $back = $header.children('.icon-arrow').first();
    const $credits = $header.children('.growtype-chat-credits').first();
    const $videocall = $header.children('.g-chat-btn-videocall').first();
    const $moreOptions = $header.children('.g-chat-btn-more-options').first();
    const $level = $header.children('.g-chat-level').first();

    if ($users.length) {
        $wrapper.insertAfter($users);
    } else if ($back.length) {
        $wrapper.insertAfter($back);
    } else if ($credits.length) {
        $wrapper.insertBefore($credits);
    } else if ($videocall.length) {
        $wrapper.insertBefore($videocall);
    } else if ($moreOptions.length) {
        $wrapper.insertBefore($moreOptions);
    } else if ($level.length) {
        $wrapper.insertAfter($level);
    } else {
        $header.append($wrapper);
    }

    return $wrapper;
}

export function restorePersistentRoleplayHeaderWrapper($chatForm, $wrapper, isActive = false) {
    if (!$chatForm?.length || !$wrapper?.length) return;
    const $header = $chatForm.find('.g-chatform-header').first();
    if (!$header.length) return;
    chatDebugLog('roleplayHeader', '[roleplay-header-debug] restore:enter', {
        isUrlOwned: isUrlOwnedRoleplaySession(),
        requestedActive: !!isActive,
        wrapperRoleplayKey: ($wrapper.attr('data-roleplay-key') || '').trim(),
    });
    logRoleplayStep('[roleplay-header] restore:start', {
        requestedActive: !!isActive,
        wrapperRoleplayKey: ($wrapper.attr('data-roleplay-key') || '').trim(),
    }, $wrapper.find('[data-roleplay-toggle="true"], [data-roleplay-settings-toggle="true"]').first(), $chatForm);

    const $existingWrapper = getCanonicalRoleplayHeaderWrapper($header);
    const $nextWrapper = $wrapper.clone();

    if ($existingWrapper.length) {
        syncRoleplayHeaderWrapperInPlace($existingWrapper, $nextWrapper);
        placeRoleplayHeaderWrapper($header, $existingWrapper);
    } else {
        placeRoleplayHeaderWrapper($header, $nextWrapper);
    }

    const $activeWrapper = $existingWrapper.length ? $existingWrapper : $nextWrapper;

    $activeWrapper.attr('data-roleplay-session-bound', isActive ? '1' : '0');
    ensureRoleplayToggleButton($activeWrapper);
    setRoleplayHeaderToggleState($activeWrapper, isActive);
    persistRoleplayHeaderWrapper($chatForm, $activeWrapper);
    logRoleplayStep('[roleplay-header] restore:complete', {
        requestedActive: !!isActive,
        wrapperRoleplayKey: ($activeWrapper.attr('data-roleplay-key') || '').trim(),
    }, $activeWrapper.find('[data-roleplay-toggle="true"], [data-roleplay-settings-toggle="true"]').first(), $chatForm);
}

export function ensurePersistentRoleplayHeaderWrapper($chatForm = null) {
    const $targetChatForm = $chatForm && $chatForm.length ? $chatForm : $('.g-chatform').first();
    if (!$targetChatForm.length) return;

    // A URL-owned full roleplay session owns its transcript and identity, so
    // never restore the persisted embedded header wrapper from localStorage there.
    const urlOwned = isUrlOwnedRoleplaySession();
    if (isChatDebugEnabled('roleplayHeader')) {
        chatDebugLog('roleplayHeader', '[roleplay-header-debug] ensure:enter', {
            isUrlOwned: urlOwned,
            locationHref: window.location.href,
        });
    }
    if (urlOwned) {
        if (isChatDebugEnabled('roleplayHeader')) {
            chatDebugLog('roleplayHeader', '[roleplay-header-debug] ensure:skip-url-owned', {
                locationHref: window.location.href,
            });
        }
        return;
    }

    const $header = $targetChatForm.find('.g-chatform-header').first();
    if (!$header.length) return;

    const $existing = getCanonicalRoleplayHeaderWrapper($header);
    if (isChatDebugEnabled('roleplayHeader')) {
        chatDebugLog('roleplayHeader', '[roleplay-header-debug] ensure:header-check', {
            hasExistingWrapper: $existing.length > 0,
        });
    }
    if ($existing.length) return;

    const $storedWrapper = loadPersistentRoleplayHeaderWrapper($targetChatForm);
    if (isChatDebugEnabled('roleplayHeader')) {
        chatDebugLog('roleplayHeader', '[roleplay-header-debug] ensure:stored-wrapper', {
            hasStoredWrapper: !!$storedWrapper?.length,
        });
    }
    if (!$storedWrapper?.length) return;

    const isActive = $targetChatForm.attr('data-roleplay') === '1';
    logRoleplayStep('[roleplay-header] ensure persistent wrapper', {
        requestedActive: isActive,
        storageKey: getPersistentRoleplayHeaderStorageKey($targetChatForm),
    }, $storedWrapper.find('[data-roleplay-toggle="true"], [data-roleplay-settings-toggle="true"]').first(), $targetChatForm);
    restorePersistentRoleplayHeaderWrapper($targetChatForm, $storedWrapper, isActive);
}

export function refreshRoleplayStoryContent($chatForm, overrides = {}, onRendered = null, onError = null, options = {}) {
    const renderArgs = getChatRenderArgs($chatForm);
    const shouldRestoreGuestHistory = options.restoreGuestHistory !== false;
    const shouldClearGuestHistoryFirst = options.clearGuestHistoryFirst === true;
    const isRoleplayActive = Boolean(overrides.roleplay_scenario && overrides.roleplay_scenario !== 'none');
    const shouldAnimateFreshMessages = typeof options.animateFreshMessages === 'boolean'
        ? options.animateFreshMessages
        : isRoleplayActive;
    const $persistentRoleplayWrapper = clonePersistentRoleplayHeaderWrapper($chatForm);
    const shouldKeepRoleplayHeaderWrapper = options.keepRoleplayHeaderWrapper === true;
    const restoredRoleplayHeaderActive = options.restoredRoleplayHeaderActive === true;
    const activatedBackgroundValue = resolveRenderableBackgroundValue(options.activatedBackgroundValue || '');
    const shouldScrollToBottom = typeof options.scrollToBottom === 'boolean'
        ? options.scrollToBottom
        : !isRoleplayActive;

    const previousScrollState = {
        chatForm: $chatForm.scrollTop(),
        content: $chatForm.find('.g-chatform-content').scrollTop(),
        page: window.pageYOffset || document.documentElement.scrollTop || 0,
    };
    const restoreScrollState = function () {
        $chatForm.scrollTop(previousScrollState.chatForm);
        if (!shouldScrollToBottom) {
            $chatForm.find('.g-chatform-content').scrollTop(previousScrollState.content);
        } else {
            scrollToChatBottom(0, true);
        }
        window.scrollTo(0, previousScrollState.page);
    };
    const overrideSessionToken = String(overrides.session_token || '').trim();
    chatDebugLog('roleplayStep', '[roleplay-trace] render:args-before-merge', {
        traceId: overrides.roleplay_trace_id || '',
        overrideSessionToken,
        formSessionToken: $chatForm.attr('data-session-token') || '',
        storedSessionToken: renderArgs.session_token || '',
        overrideRoleplayScenario: overrides.roleplay_scenario || '',
    });
    const sanitizedArgs = {
        ...renderArgs,
        ...overrides,
        session_token: overrideSessionToken || $chatForm.attr('data-session-token') || renderArgs.session_token || '',
        user_token: $chatForm.attr('data-user-token') || renderArgs.user_token || '',
        intro_quiz: '',
        roleplay_quiz_slug: '',
    };

    const serializedArgs = JSON.stringify(sanitizedArgs);
    const lightweightSerializedArgs = JSON.stringify(buildLightweightChatRenderArgs(sanitizedArgs));
    $chatForm.attr('data-chat-render-args', serializedArgs);
    syncChatContentRequestContext($chatForm, sanitizedArgs);
    invalidateChatContentRequests($chatForm, {
        reason: 'roleplay-refresh',
    });
    chatDebugLog('roleplayStep', '[roleplay-trace] render:request-ready', {
        traceId: sanitizedArgs.roleplay_trace_id || '',
        sessionToken: sanitizedArgs.session_token,
        roleplayScenario: sanitizedArgs.roleplay_scenario || '',
        formSessionToken: $chatForm.attr('data-session-token') || '',
    });

    if (shouldClearGuestHistoryFirst) {
        clearGuestLocalHistory($chatForm);
    }

    chatDebugLog('roleplayStep', '[roleplay-story-start] refresh-chat-content', {
        sanitizedArgs,
        shouldRestoreGuestHistory,
        shouldClearGuestHistoryFirst,
    });

    const $existingAudioPlayer = $chatForm.find('.speech-audio-player').first().clone();

    $.ajax({
        url: growtype_chat_ajax.url,
        type: 'post',
        data: {
            action: 'chat_content_render',
            args: lightweightSerializedArgs,
            nonce: growtype_chat_ajax.nonce,
        },
        success: function (response) {
            chatDebugLog('roleplayStep', '[roleplay-trace] render:response', {
                traceId: sanitizedArgs.roleplay_trace_id || '',
                success: response?.success === true,
                requestedSessionToken: sanitizedArgs.session_token,
                hasRender: Boolean(response?.data?.render),
            });
            chatDebugLog('roleplayStep', '[roleplay-story-start] refresh-chat-content:success', response);

            if (!response || response.success !== true || !response.data?.render) {
                console.error('[roleplay-story-start] refresh-chat-content:invalid-response', response);
                if (typeof onError === 'function') onError(response);
                return;
            }

            updateCredits(response.data.existing_credits);
            const previousChatConfig = window.growtype_chat?.[$chatForm.attr('id')] || null;

            $chatForm.html(prepareRoleplayStoryRefreshRender(response.data.render, {
                animateFreshMessages: shouldAnimateFreshMessages,
            }));

            const refreshedChatId = $chatForm.attr('id');
            if (
                previousChatConfig &&
                refreshedChatId &&
                window.growtype_chat?.[refreshedChatId] &&
                !window.growtype_chat[refreshedChatId].bot_profile
            ) {
                window.growtype_chat[refreshedChatId].bot_profile =
                    previousChatConfig.bot_profile || '';
            }
            restoreScrollState();

            if (activatedBackgroundValue && typeof window.updateChatBackground === 'function') {
                window.updateChatBackground(activatedBackgroundValue, {}, true);
            }

            if (shouldKeepRoleplayHeaderWrapper && $persistentRoleplayWrapper?.length) {
                restorePersistentRoleplayHeaderWrapper(
                    $chatForm,
                    $persistentRoleplayWrapper,
                    restoredRoleplayHeaderActive
                );
            }

            if ($existingAudioPlayer.length) {
                $chatForm.append($existingAudioPlayer);
            }

            if (shouldRestoreGuestHistory) {
                restoreGuestLocalHistory($chatForm);
                restoreScrollState();
            }

            ensurePersistentRoleplayHeaderWrapper($chatForm);
            initMessageActions($chatForm);

            revealRoleplayStoryRefreshedMessages($chatForm, {
                animateFreshMessages: shouldAnimateFreshMessages,
            }).finally(function () {
                if (typeof onRendered === 'function') {
                    onRendered(response, $chatForm);
                }

                document.dispatchEvent(new CustomEvent('growtypeChatContentRendered', {
                    detail: {
                        contentWrapper: $chatForm,
                        messages: []
                    }
                }));

                window.requestAnimationFrame(function () {
                    if (shouldScrollToBottom) {
                        scrollToChatBottom(0, true);
                        return;
                    }
                    restoreScrollState();
                });
            });
        },
        error: function (xhr, status, error) {
            console.error('[roleplay-story-start] refresh-chat-content:error', {
                status,
                error,
                responseText: xhr?.responseText,
            });
            if (typeof onError === 'function') {
                onError({ xhr, status, error });
            }
        }
    });
}

export function handleRoleplayStartStoryClick(event) {
    event.preventDefault();

    const $button = $(event.currentTarget);
    const $chatForm = $button.closest('.g-chatform');
    const sessionToken = getSessionToken($chatForm);
    const hideUntilMessageId = String(
        $button.closest('.g-chatform-message').attr('data-id') || ''
    ).trim();
    const ajaxAction = window.growtype_chat_ajax?.roleplay_start_story_action || 'growtype_chat_roleplay_start_story';

    if ($button.data('roleplayStoryStartLoading') === true) return;

    chatDebugLog('roleplayStep', '[roleplay-story-start] click', {
        sessionToken,
        hideUntilMessageId,
        ajaxAction,
        currentUrl: window.location.href,
    });

    $button.data('roleplayStoryStartLoading', true);
    $button.prop('disabled', true).attr('aria-disabled', 'true');

    $.ajax({
        url: growtype_chat_ajax.url,
        type: 'post',
        data: {
            action: ajaxAction,
            nonce: growtype_chat_ajax.nonce,
            session_token: sessionToken,
            current_url: window.location.href,
            hide_until_message_id: hideUntilMessageId,
        },
        success: function (response) {
            chatDebugLog('roleplayStep', '[roleplay-story-start] success', response);

            if (!response || response.success !== true) {
                clearRoleplayStoryStarted(sessionToken);
                $button.data('roleplayStoryStartLoading', false);
                $button.prop('disabled', false).removeAttr('aria-disabled');
                handleAjaxError(response, $button);
                return;
            }

            markRoleplayStoryStarted(sessionToken);
            clearIntroQuizCookie();
            const currentRenderArgs = getChatRenderArgs($chatForm);
            const generationPrompt = String(response?.data?.generation_prompt || '').trim();
            const nextUrl = resolveRoleplayBrowserUrl(
                response?.data?.next_url || window.location.href,
                {
                    isActive: true,
                    roleplay_session_mode: response?.data?.roleplay_session_mode || '',
                }
            );
            const nextRoleplayScenario = new URL(nextUrl, window.location.origin).searchParams.get('roleplay_scenario')
                || currentRenderArgs.roleplay_scenario
                || '';

            window.history.replaceState({}, '', nextUrl);
            $chatForm.attr('data-chat-render-args', JSON.stringify({
                ...currentRenderArgs,
                roleplay_scenario: nextRoleplayScenario,
                intro_quiz: '',
                roleplay_quiz_slug: '',
            }));
            const $summaryMessage = $button.closest('.g-chatform-message');
            if (generationPrompt === '') {
                $button.data('roleplayStoryStartLoading', false);
                $button.prop('disabled', false).removeAttr('aria-disabled');
                handleAjaxError({
                    success: false,
                    data: { message: 'Could not start the conversation.' },
                }, $button);
                return;
            }

            $summaryMessage.remove();
            submitChatMessages({
                0: {
                    value: generationPrompt,
                    category: 'text',
                    skip_saving: true,
                    skip_local_history: true,
                    skip_question_preview: true,
                    skip_response_extensions: true,
                    reuse_pending_loader: false,
                    interaction_type: 'composer',
                    frontend_delay_case: 'inline_quiz_answer',
                },
            }, $chatForm);
        },
        error: function (xhr, status, error) {
            console.error('[roleplay-story-start] error', { status, error, responseText: xhr?.responseText });
            clearRoleplayStoryStarted(sessionToken);
            $button.data('roleplayStoryStartLoading', false);
            $button.prop('disabled', false).removeAttr('aria-disabled');
        }
    });
}

export function initRoleplayStartStoryActions() {
    $(document)
        .off('click.roleplayStartStory', '[data-roleplay-start-story="true"]')
        .on('click.roleplayStartStory', '[data-roleplay-start-story="true"]', handleRoleplayStartStoryClick);
}

export function getBackgroundStateController() {
    if (!window.growtypeChatBackgroundState || typeof window.growtypeChatBackgroundState !== 'object') {
        return null;
    }
    return window.growtypeChatBackgroundState;
}

export function getBaseBackgroundValue() {
    const controller = getBackgroundStateController();
    if (!controller || typeof controller.getBaseBackgroundValue !== 'function') return '';
    return controller.getBaseBackgroundValue() || '';
}

export function restoreBaseBackgroundState({ forceApply = false } = {}) {
    const controller = getBackgroundStateController();
    if (!controller || typeof controller.restoreBaseBackgroundState !== 'function') return '';
    return controller.restoreBaseBackgroundState({ forceApply }) || '';
}

export function requestRoleplayStop({
    chatForm,
    submitBtn,
    roleplayScenario = '',
    backgroundValue = '',
    onSuccess = null,
    onError = null,
}) {
    const sessionToken = getSessionToken(chatForm);
    const traceId = `rp-stop-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const renderArgs = getChatRenderArgs(chatForm);
    logRoleplayStep('[roleplay-stop] ajax:prepare', {
        sessionToken,
        roleplayScenario,
        backgroundValue,
    }, submitBtn, chatForm);

    const requestRegistry = window.__growtypeTalkiemateRoleplayStopRequests
        || (window.__growtypeTalkiemateRoleplayStopRequests = {});

    if (!sessionToken) {
        logRoleplayStep('[roleplay-stop] ajax:missing-session', {
            roleplayScenario,
            backgroundValue,
        }, submitBtn, chatForm);
        if (typeof onError === 'function') {
            onError({
                success: false,
                data: {
                    message: 'Missing session token.',
                },
            });
        }
        return false;
    }

    if (requestRegistry[sessionToken] === true) {
        logRoleplayStep('[roleplay-stop] ajax:duplicate-request', {
            sessionToken,
            roleplayScenario,
        }, submitBtn, chatForm);

        if (typeof onError === 'function') {
            onError({
                success: false,
                duplicate: true,
                data: {
                    message: 'Roleplay stop request is already running.',
                },
            });
        }
        return false;
    }

    requestRegistry[sessionToken] = true;
    logRoleplayStep('[roleplay-stop] ajax:dispatch', {
        sessionToken,
        roleplayScenario,
        backgroundValue,
        ajaxAction: growtype_chat_ajax.roleplay_stop_action || 'growtype_chat_stop_roleplay',
    }, submitBtn, chatForm);

    return $.ajax({
        url: growtype_chat_ajax.url,
        type: 'post',
        data: {
            session_token: sessionToken,
            user_token: chatForm?.attr('data-user-token') || '',
            normal_session_token: chatForm?.attr('data-normal-session-token') || '',
            roleplay_scenario: roleplayScenario,
            chat_background_value: backgroundValue,
            roleplay_trace_id: traceId,
            render_args: JSON.stringify(renderArgs),
            action: growtype_chat_ajax.roleplay_stop_action || 'growtype_chat_stop_roleplay',
            nonce: growtype_chat_ajax.nonce,
        },
        success: function (response) {
            logRoleplayStep('[roleplay-stop] ajax:success', {
                responseSuccess: response?.success === true,
                responseDataKeys: Object.keys(response?.data || {}),
            }, submitBtn, chatForm);
            if (!response?.success) {
                alert(response?.data?.message || 'Error stopping roleplay');
                submitBtn?.removeClass('is-loading');
                requestRegistry[sessionToken] = false;
                logRoleplayStep('[roleplay-stop] ajax:success-invalid-response', {
                    responseMessage: response?.data?.message || '',
                }, submitBtn, chatForm);
                if (typeof onError === 'function') onError(response);
                return;
            }
            if (typeof onSuccess === 'function') onSuccess(response);
            requestRegistry[sessionToken] = false;
            logRoleplayStep('[roleplay-stop] ajax:success-complete', {
                registryReleased: requestRegistry[sessionToken] === false,
            }, submitBtn, chatForm);
        },
        error: function (response) {
            chatDebugLog('roleplayStep', '[roleplay-trace] render:error', {
                traceId: 'unknown',
                status: response?.status || null,
                responseText: response?.responseText || '',
            });
            requestRegistry[sessionToken] = false;
            logRoleplayStep('[roleplay-stop] ajax:error', {
                status: response?.status || null,
                responseText: response?.responseText || '',
            }, submitBtn, chatForm);
            if (typeof onError === 'function') {
                onError(response);
                return;
            }
            handleAjaxError(response, submitBtn);
        }
    });
}

export function requestRoleplayStart({
    chatForm,
    submitBtn,
    roleplayScenario = '',
    onSuccess = null,
    onError = null,
}) {
    const sessionToken = getSessionToken(chatForm);
    const traceId = `rp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const renderArgs = getChatRenderArgs(chatForm);
    chatDebugLog('roleplayStep', '[roleplay-trace] start:prepare', {
        traceId,
        sessionToken,
        roleplayScenario,
        formSessionToken: chatForm?.attr('data-session-token') || '',
        shellSessionToken: window.growtype_chat?.['gc-shell-chat']?.session_token || '',
    });
    logRoleplayStep('[roleplay-start] ajax:prepare', {
        sessionToken,
        roleplayScenario,
    }, submitBtn, chatForm);
    if (!sessionToken || !roleplayScenario) {
        logRoleplayStep('[roleplay-start] ajax:blocked', {
            missingSession: !sessionToken,
            missingRoleplayScenario: !roleplayScenario,
        }, submitBtn, chatForm);
        return;
    }

    $.ajax({
        url: growtype_chat_ajax.url,
        type: 'post',
        data: {
            session_token: sessionToken,
            roleplay_scenario: roleplayScenario,
            roleplay_trace_id: traceId,
            render_args: JSON.stringify(renderArgs),
            user_token: chatForm?.attr('data-user-token') || window.growtype_chat?.['gc-shell-chat']?.user_token || (typeof growtype_chat_get_user_token === 'function' ? growtype_chat_get_user_token() : ''),
            action: growtype_chat_ajax.roleplay_resume_action || 'growtype_chat_start_roleplay',
            nonce: growtype_chat_ajax.nonce,
        },
        success: function (response) {
            chatDebugLog('roleplayStep', '[roleplay-trace] start:response', {
                traceId,
                responseTraceId: response?.data?.roleplay_trace_id || '',
                success: response?.success === true,
                sourceSessionToken: sessionToken,
                targetSessionId: response?.data?.session_id || '',
                targetSessionToken: response?.data?.session_token || '',
                targetIsPending: String(response?.data?.session_token || '').startsWith('PENDING:'),
            });
            logRoleplayStep('[roleplay-start] ajax:success', {
                responseSuccess: response?.success === true,
                responseDataKeys: Object.keys(response?.data || {}),
            }, submitBtn, chatForm);
            if (!response?.success) {
                logRoleplayStep('[roleplay-start] ajax:success-invalid-response', {
                    responseMessage: response?.data?.message || '',
                }, submitBtn, chatForm);
                if (typeof onError === 'function') onError(response);
                handleAjaxError(response, submitBtn);
                return;
            }
            if (typeof onSuccess === 'function') onSuccess(response);
        },
        error: function (response) {
            logRoleplayStep('[roleplay-start] ajax:error', {
                status: response?.status || null,
                responseText: response?.responseText || '',
            }, submitBtn, chatForm);
            if (typeof onError === 'function') {
                onError(response);
            }
            handleAjaxError(response, submitBtn);
        }
    });
}

export function requireLoginForRoleplayStart(submitBtn = null) {
    if (window.growtype_chat_ajax?.is_logged_in) {
        return false;
    }

    handleAjaxError({
        responseJSON: {
            data: {
                message: ACCESS_REQUIREMENT_CODES.PROFILE_REQUIRED,
            },
        },
    }, submitBtn);

    return true;
}

export function updateAllRoleplayToggleStates(isActive, roleplayKey = '', options = {}) {
    const active = !!isActive;
    const label = active ? 'Turn roleplay off' : 'Turn roleplay on';
    const requestState = normalizeRoleplayRequestState(options.requestState);
    const isLoading = requestState !== ROLEPLAY_REQUEST_STATES.IDLE;
    logRoleplayStep('[roleplay-toggle] update all states:start', {
        requestedActive: active,
        roleplayKey,
        requestState,
        toggleCount: $('[data-roleplay-toggle="true"], [data-roleplay-settings-toggle="true"], .roleplay-details-toggle').length,
    });

    $('[data-roleplay-toggle="true"], [data-roleplay-settings-toggle="true"], .roleplay-details-toggle').each(function () {
        const $toggle = $(this);
        $toggle
            .attr('data-roleplay-active', active ? '1' : '0')
            .attr('aria-pressed', active ? 'true' : 'false')
            .attr('aria-label', label)
            .attr('title', label)
            .attr('data-roleplay-request-state', requestState)
            .attr('aria-busy', isLoading ? 'true' : 'false')
            .toggleClass('is-loading', isLoading)
            .toggleClass('is-active', active);

        if (isLoading) {
            $toggle.attr('aria-disabled', 'true');
        } else {
            $toggle.removeAttr('aria-disabled');
        }

        if (roleplayKey && roleplayKey !== 'none') {
            $toggle.attr('data-roleplay-key', roleplayKey);
        }

        const $checkbox = $toggle.find('input[type="checkbox"]').first();
        if ($checkbox.length) {
            $checkbox.prop('checked', active);
            if (active) {
                $checkbox.attr('checked', 'checked');
            } else {
                $checkbox.removeAttr('checked');
            }
        }
    });

    if (typeof syncRoleplaySettingsToggleState === 'function') {
        syncRoleplaySettingsToggleState(active, { requestState, roleplayKey });
    }

    logRoleplayStep('[roleplay-toggle] update all states:complete', {
        requestedActive: active,
        roleplayKey,
        requestState,
    });
}

export function getRoleplayToggleDebugState($button, $chatForm = null) {
    const $targetButton = $button?.length ? $button : $();
    const $targetChatForm = $chatForm?.length
        ? $chatForm
        : ($targetButton.closest('.g-chatform').length ? $targetButton.closest('.g-chatform') : $('.g-chatform').first());
    const $wrapper = $targetButton.closest('.roleplay-details-wrapper');
    const $checkbox = $targetButton.find('input[type="checkbox"]').first();

    return {
        sessionToken: getSessionToken($targetChatForm),
        requestState: getRoleplayRequestState($targetChatForm),
        roleplayKey: ($targetButton.attr('data-roleplay-key') || '').trim(),
        buttonActive: $targetButton.attr('data-roleplay-active'),
        buttonAriaPressed: $targetButton.attr('aria-pressed'),
        buttonClass: $targetButton.attr('class') || '',
        chatFormRoleplay: $targetChatForm.attr('data-roleplay'),
        sessionBound: $wrapper.attr('data-roleplay-session-bound'),
        wrapperClass: $wrapper.attr('class') || '',
        checkboxCheckedProp: $checkbox.length ? $checkbox.prop('checked') : null,
        checkboxCheckedAttr: $checkbox.length ? $checkbox.attr('checked') || null : null,
        checkboxAriaHidden: $checkbox.length ? $checkbox.attr('aria-hidden') || null : null,
        startLoading: $targetButton.data('roleplayStartLoading') === true,
        exitLoading: $targetButton.data('roleplayExitLoading') === true,
        isLoadingClass: $targetButton.hasClass('is-loading'),
        isActiveClass: $targetButton.hasClass('is-active'),
    };
}

export function logRoleplayStep(step, details = {}, $button = null, $chatForm = null) {
    const debugState = $button?.length || $chatForm?.length
        ? getRoleplayToggleDebugState($button, $chatForm)
        : null;

    chatDebugLog('roleplayStep', step, {
        ...details,
        debugState,
        locationHref: window.location.href,
    });
}

export function applyRoleplayUiState($chatForm, {
    isActive = false,
    roleplayKey = '',
    requestState = ROLEPLAY_REQUEST_STATES.IDLE,
    persist = true,
} = {}) {
    const $targetChatForm = $chatForm && $chatForm.length ? $chatForm : $('.g-chatform').first();
    if (!$targetChatForm.length) return;

    const normalizedRequestState = normalizeRoleplayRequestState(requestState);
    const active = !!isActive;
    const $wrapper = getCanonicalRoleplayHeaderWrapper(
        $targetChatForm.find('.g-chatform-header').first()
    );

    $targetChatForm
        .attr('data-roleplay', active ? '1' : '0')
        .attr('data-roleplay-request-state', normalizedRequestState);

    if (roleplayKey && roleplayKey !== 'none') {
        $targetChatForm.attr('data-roleplay-key', roleplayKey);
    }

    if ($wrapper.length) {
        $wrapper
            .attr('data-roleplay-session-bound', active ? '1' : '0')
            .attr('data-roleplay-request-state', normalizedRequestState);

        if (roleplayKey && roleplayKey !== 'none') {
            $wrapper.attr('data-roleplay-key', roleplayKey);
        }
    }

    updateAllRoleplayToggleStates(active, roleplayKey, {
        requestState: normalizedRequestState,
    });

    if ($wrapper.length && persist) {
        persistRoleplayHeaderWrapper($targetChatForm, $wrapper);
    }

    logRoleplayStep('[roleplay-state] apply ui state', {
        requestedActive: active,
        roleplayKey,
        requestState: normalizedRequestState,
        persisted: persist,
    }, $wrapper.find('[data-roleplay-toggle="true"], [data-roleplay-settings-toggle="true"]').first(), $targetChatForm);
}

export function setRoleplayHeaderToggleState($wrapper, isActive, options = {}) {
    const $chatForm = $wrapper?.closest('.g-chatform');
    const $toggle = $wrapper?.find('[data-roleplay-toggle="true"], [data-roleplay-settings-toggle="true"]').first();
    const requestState = normalizeRoleplayRequestState(options.requestState);
    const roleplayKey = (options.roleplayKey || $wrapper?.attr('data-roleplay-key') || '').trim();

    logRoleplayStep('[roleplay-header] set toggle state:start', {
        requestedActive: !!isActive,
        wrapperRoleplayKey: ($wrapper?.attr('data-roleplay-key') || '').trim(),
        requestState,
    }, $toggle, $chatForm);

    updateAllRoleplayToggleStates(isActive, roleplayKey, { requestState });
    if (!$wrapper || !$wrapper.length) return;
    if (!$toggle.length) return;

    const active = !!isActive;
    const label = active ? 'Turn roleplay off' : 'Turn roleplay on';

    $toggle
        .attr('data-roleplay-active', active ? '1' : '0')
        .attr('aria-pressed', active ? 'true' : 'false')
        .attr('aria-label', label)
        .attr('title', label)
        .attr('data-roleplay-request-state', requestState)
        .attr('aria-busy', requestState !== ROLEPLAY_REQUEST_STATES.IDLE ? 'true' : 'false')
        .toggleClass('is-loading', requestState !== ROLEPLAY_REQUEST_STATES.IDLE)
        .toggleClass('is-active', active);

    if (requestState !== ROLEPLAY_REQUEST_STATES.IDLE) {
        $toggle.attr('aria-disabled', 'true');
    } else {
        $toggle.removeAttr('aria-disabled');
    }

    $toggle.find('input[type="checkbox"]').first()
        .prop('checked', active)
        .attr('checked', active ? 'checked' : null);

    logRoleplayStep('[roleplay-header] set toggle state:complete', {
        requestedActive: active,
        wrapperRoleplayKey: ($wrapper.attr('data-roleplay-key') || '').trim(),
        requestState,
    }, $toggle, $chatForm);
}

export function beginOptimisticRoleplayStart($button, $chatForm, fromPointer = false) {
    const $wrapper = $button.closest('.roleplay-details-wrapper');
    const roleplayKey = ($button.attr('data-roleplay-key') || $wrapper.attr('data-roleplay-key') || '').trim();

    logRoleplayStep('[roleplay-start] optimistic:start', {
        fromPointer,
    }, $button, $chatForm);

    if (fromPointer) {
        $button.data('roleplayStartIntent', true);
    }
    $button.data('roleplayStartLoading', true);
    $button.data('roleplayExitLoading', false);

    // Show spinner on ALL roleplay toggles WITHOUT switching them ON.
    // The toggle will flip ON only when the AJAX response returns successfully.
    $chatForm.attr('data-roleplay-request-state', ROLEPLAY_REQUEST_STATES.STARTING);
    $('[data-roleplay-toggle="true"], [data-roleplay-settings-toggle="true"], .roleplay-details-toggle').each(function () {
        $(this)
            .addClass('is-loading')
            .attr('aria-busy', 'true')
            .attr('data-roleplay-request-state', ROLEPLAY_REQUEST_STATES.STARTING)
            .attr('aria-disabled', 'true');
    });

    logRoleplayStep('[roleplay-start] optimistic:complete', {
        fromPointer,
    }, $button, $chatForm);
}

export function revertOptimisticRoleplayStart($button, $chatForm) {
    logRoleplayStep('[roleplay-start] optimistic:revert:start', {}, $button, $chatForm);
    $button.removeData('roleplayStartIntent');
    $button.data('roleplayStartLoading', false);
    applyRoleplayUiState($chatForm, {
        isActive: false,
        requestState: ROLEPLAY_REQUEST_STATES.IDLE,
    });
    logRoleplayStep('[roleplay-start] optimistic:revert:complete', {}, $button, $chatForm);
}

export function beginOptimisticRoleplayStop($button, $chatForm, nextUrl = '') {
    const roleplayKey = ($button.attr('data-roleplay-key') || $chatForm.attr('data-roleplay-key') || '').trim();

    chatDebugLog('roleplayStep', '[roleplay-stop] request loading start', {
        nextUrl,
        before: getRoleplayToggleDebugState($button, $chatForm),
    });

    applyRoleplayUiState($chatForm, {
        isActive: false,
        roleplayKey,
        requestState: ROLEPLAY_REQUEST_STATES.STOPPING,
    });

    chatDebugLog('roleplayStep', '[roleplay-stop] optimistic off applied', {
        nextUrl,
        after: getRoleplayToggleDebugState($button, $chatForm),
    });

    window.setTimeout(function () {
        chatDebugLog('roleplayStep', '[roleplay-stop] optimistic off +0ms', {
            nextUrl,
            snapshot: getRoleplayToggleDebugState($button, $chatForm),
        });
    }, 0);

    window.setTimeout(function () {
        chatDebugLog('roleplayStep', '[roleplay-stop] optimistic off +150ms', {
            nextUrl,
            snapshot: getRoleplayToggleDebugState($button, $chatForm),
        });
    }, 150);

    $button.data('roleplayExitLoading', true);
    $button.data('roleplayStartLoading', false);

    chatDebugLog('roleplayStep', '[roleplay-stop] loading flag applied', {
        nextUrl,
        afterLoading: getRoleplayToggleDebugState($button, $chatForm),
    });
}

export function revertOptimisticRoleplayStop($button, $chatForm, roleplayKey = '') {
    chatDebugLog('roleplayStep', '[roleplay-stop] request loading revert', {
        roleplayKey,
        before: getRoleplayToggleDebugState($button, $chatForm),
    });

    $button.data('roleplayExitLoading', false);
    applyRoleplayUiState($chatForm, {
        isActive: true,
        roleplayKey,
        requestState: ROLEPLAY_REQUEST_STATES.IDLE,
    });

    chatDebugLog('roleplayStep', '[roleplay-stop] request loading reverted state', {
        roleplayKey,
        after: getRoleplayToggleDebugState($button, $chatForm),
    });
}

export function completeRoleplayStopUi($button, $chatForm, nextUrl = '') {
    chatDebugLog('roleplayStep', '[roleplay-stop] ui complete', {
        nextUrl,
        before: getRoleplayToggleDebugState($button, $chatForm),
    });

    applyRoleplayUiState($chatForm, {
        isActive: false,
        requestState: ROLEPLAY_REQUEST_STATES.IDLE,
    });
    clearRoleplayHeaderUI();
    restoreBaseBackgroundState({ forceApply: true });

    if (nextUrl) {
        window.history.replaceState({}, '', nextUrl);
    }

    if (typeof window.syncActiveChatSession === 'function') {
        window.syncActiveChatSession();
    }

    chatDebugLog('roleplayStep', '[roleplay-stop] ui complete applied', {
        nextUrl,
        after: getRoleplayToggleDebugState($button, $chatForm),
    });
}

export function clearRoleplayHeaderUI() {
    logRoleplayStep('[roleplay-header] clear ui:start');
    $('.g-chatform')
        .attr('data-roleplay', '0')
        .attr('data-roleplay-request-state', ROLEPLAY_REQUEST_STATES.IDLE);

    $('.g-chatform-header').each(function () {
        const $wrapper = getCanonicalRoleplayHeaderWrapper($(this));
        if (!$wrapper.length) return;

        $wrapper.find('[data-roleplay-exit="true"]').remove();
        $wrapper
            .attr('data-roleplay-session-bound', '0')
            .attr('data-roleplay-request-state', ROLEPLAY_REQUEST_STATES.IDLE);
        setRoleplayHeaderToggleState($wrapper, false, {
            requestState: ROLEPLAY_REQUEST_STATES.IDLE,
        });
        persistRoleplayHeaderWrapper($wrapper.closest('.g-chatform'), $wrapper);
    });
    logRoleplayStep('[roleplay-header] clear ui:complete');
}

export function syncRoleplayStateWithHeader($chatForm = null) {
    const $targetChatForm = $chatForm && $chatForm.length ? $chatForm : $('.g-chatform').first();
    if (!$targetChatForm.length) return false;

    const $header = $targetChatForm.find('.g-chatform-header').first();
    const $wrapper = getCanonicalRoleplayHeaderWrapper($header);
    const $toggle = $wrapper.find('[data-roleplay-toggle="true"]').first();
    const hasRoleplayWrapper = $wrapper.length > 0;
    const requestState = getRoleplayRequestState($targetChatForm);
    const isRoleplayActive = $targetChatForm.attr('data-roleplay') === '1';
    const roleplayKey = (
        $targetChatForm.attr('data-roleplay-key')
        || $wrapper.attr('data-roleplay-key')
        || $toggle.attr('data-roleplay-key')
        || ''
    ).trim();

    if (hasRoleplayWrapper) {
        placeRoleplayHeaderWrapper($header, $wrapper);
        $wrapper
            .attr('data-roleplay-session-bound', isRoleplayActive ? '1' : '0')
            .attr('data-roleplay-request-state', requestState);
        if (roleplayKey) {
            $wrapper.attr('data-roleplay-key', roleplayKey);
        }
        setRoleplayHeaderToggleState($wrapper, isRoleplayActive, {
            requestState,
            roleplayKey,
        });
        persistRoleplayHeaderWrapper($targetChatForm, $wrapper);
    }

    $targetChatForm
        .attr('data-roleplay', isRoleplayActive ? '1' : '0')
        .attr('data-roleplay-request-state', requestState);
    logRoleplayStep('[roleplay-header] sync with header', {
        hasRoleplayWrapper,
        isRoleplayActive,
        requestState,
        wrapperRoleplayKey: ($wrapper.attr('data-roleplay-key') || '').trim(),
    }, $toggle, $targetChatForm);

    return hasRoleplayWrapper && isRoleplayActive;
}

export function updateSessionTokenReferences($chatForm, sessionToken) {
    const nextSessionToken = (sessionToken || '').trim();
    if (!nextSessionToken) return;

    if ($chatForm?.length) {
        $chatForm.attr('data-session-token', nextSessionToken);
    }

    $('[data-session-token]').each(function () {
        const $element = $(this);
        if ($element.closest('.g-chatform').is($chatForm) || $element.is($chatForm)) {
            $element.attr('data-session-token', nextSessionToken);
        }
    });

    if (window.growtype_chat?.['gc-shell-chat']) {
        window.growtype_chat['gc-shell-chat'].session_token = nextSessionToken;
    }
}

export function syncResponseCharacterIdentity($chatForm, responseCharacter) {
    if (!responseCharacter || typeof responseCharacter !== 'object') return;
    const chatId = $chatForm.attr('id');
    if (!chatId || !window.growtype_chat?.[chatId]) return;

    const chatConfig = window.growtype_chat[chatId];
    const botProfile = chatConfig.bot_profile && typeof chatConfig.bot_profile === 'object'
        ? chatConfig.bot_profile
        : {};

    chatConfig.bot_profile = {
        ...botProfile,
        response_character: responseCharacter,
        name: responseCharacter.name || botProfile.name || '',
    };
}

export function ensureRoleplayToggleButton($wrapper) {
    if (!$wrapper || !$wrapper.length) return;
    if ($wrapper.find('[data-roleplay-toggle="true"], [data-roleplay-settings-toggle="true"]').length) return;

    const roleplayKey = ($wrapper.attr('data-roleplay-key') || '').trim();
    if (!roleplayKey) return;

    const label = 'Turn roleplay off';
    const $switchButton = $('<div class="btn-toggle btn-toggle-sm roleplay-details-toggle" type="roleplay_toggle" data-roleplay-toggle="true" data-roleplay-active="1" aria-pressed="true"></div>');

    $switchButton.attr('data-roleplay-key', roleplayKey);
    $switchButton.attr('aria-label', label);
    $switchButton.attr('title', label);
    $switchButton.append('<div class="btn-toggle-inner"><input type="checkbox" name="roleplay_toggle_state" value="1" class="checkbox" checked aria-hidden="true" tabindex="-1"><div class="knobs" data-label-off="off" data-label-on="on"></div><div class="layer"></div></div>');

    $wrapper.append($switchButton);
    persistRoleplayHeaderWrapper($wrapper.closest('.g-chatform'), $wrapper);
    logRoleplayStep('[roleplay-header] ensure toggle button:created', {
        wrapperRoleplayKey: roleplayKey,
    }, $switchButton, $wrapper.closest('.g-chatform'));
}

export function ensureAllRoleplayToggleButtons($scope = $(document)) {
    $scope.find('.g-chatform').each(function () {
        const $chatForm = $(this);
        ensurePersistentRoleplayHeaderWrapper($chatForm);
        ensureRoleplayToggleButton(getCanonicalRoleplayHeaderWrapper(
            $chatForm.find('.g-chatform-header').first()
        ));
        syncRoleplayStateWithHeader($chatForm);
    });
}

export function startRoleplayInCurrentChat({
    $button = $(),
    $chatForm = null,
    roleplayScenario = '',
    $modalToClose = null,
    fallbackUrl = '',
} = {}) {
    const $targetButton = $button?.length ? $button : $();
    const $targetChatForm = $chatForm?.length ? $chatForm : $('.g-chatform').first();
    const $targetModal = $modalToClose?.length ? $modalToClose : $('#growtypeChatSettingsModal');
    const $roleplaySelect = $('select[name="roleplay_scenario"]').first();
    const currentRoleplayValue = (
        roleplayScenario
        || $targetButton.attr('data-roleplay-key')
        || $targetChatForm.attr('data-roleplay-key')
        || ''
    ).trim();
    const requestState = getRoleplayRequestState($targetChatForm);

    logRoleplayStep('[roleplay-preview-start] request:entry', {
        hasChatForm: $targetChatForm.length,
        roleplayScenario: currentRoleplayValue,
        requestState,
        fallbackUrl,
    }, $targetButton, $targetChatForm);

    if (!$targetChatForm.length) {
        if (fallbackUrl) {
            window.location.href = fallbackUrl;
        }
        return false;
    }

    if (requestState !== ROLEPLAY_REQUEST_STATES.IDLE) {
        logRoleplayStep('[roleplay-preview-start] request blocked while processing', {
            requestState,
        }, $targetButton, $targetChatForm);
        return false;
    }

    if (requireLoginForRoleplayStart($targetButton)) {
        logRoleplayStep('[roleplay-preview-start] request blocked by login requirement', {}, $targetButton, $targetChatForm);
        return false;
    }

    const sessionToken = getSessionToken($targetChatForm);
    const startBlockedByMissingSession = !sessionToken;
    const startBlockedByMissingRoleplay = !currentRoleplayValue;
    const startBlockedByLoading = $targetButton.data('roleplayStartLoading') === true;

    if (startBlockedByMissingSession || startBlockedByMissingRoleplay || startBlockedByLoading) {
        logRoleplayStep('[roleplay-preview-start] request blocked', {
            sessionToken,
            roleplayKey: currentRoleplayValue,
            missingSessionToken: startBlockedByMissingSession,
            missingRoleplayKey: startBlockedByMissingRoleplay,
            blockedByStartLoading: startBlockedByLoading,
        }, $targetButton, $targetChatForm);

        if (!sessionToken && fallbackUrl) {
            window.location.href = fallbackUrl;
        }

        return false;
    }

    if ($targetModal.length) {
        logRoleplayStep('[roleplay-preview-start] modal close before request', {
            modalId: $targetModal.attr('id') || '',
        }, $targetButton, $targetChatForm);
        forceCloseModal($targetModal);
    }

    beginOptimisticRoleplayStart($targetButton, $targetChatForm);

    const roleplayTransition = showRoleplayTransitionLoader($targetChatForm);
    logRoleplayStep('[roleplay-preview-start] transition loader shown', {
        hasTransitionMessage: !!roleplayTransition?.$message?.length,
        minimumDuration: roleplayTransition?.minimumDuration || 0,
    }, $targetButton, $targetChatForm);

    requestRoleplayStart({
        chatForm: $targetChatForm,
        submitBtn: $targetButton,
        roleplayScenario: currentRoleplayValue,
        onSuccess: function (response) {
            logRoleplayStep('[roleplay-preview-start] onSuccess:entered', {
                responseDataKeys: Object.keys(response?.data || {}),
            }, $targetButton, $targetChatForm);

            completeRoleplayTransition(roleplayTransition, function () {
                syncResponseCharacterIdentity($targetChatForm, response?.data?.response_character);
                applyRoleplayUiState($targetChatForm, {
                    isActive: true,
                    roleplayKey: currentRoleplayValue,
                    requestState: ROLEPLAY_REQUEST_STATES.IDLE,
                });

                if (typeof syncRoleplaySettingsToggleState === 'function') {
                    syncRoleplaySettingsToggleState(true, {
                        requestState: ROLEPLAY_REQUEST_STATES.IDLE,
                        roleplayKey: currentRoleplayValue,
                    });
                }

                if ($roleplaySelect.length && currentRoleplayValue && currentRoleplayValue !== 'none') {
                    $roleplaySelect.val(currentRoleplayValue);
                    if ($.fn.chosen) {
                        $roleplaySelect.trigger('chosen:updated');
                    }
                }

                $targetChatForm.attr('data-roleplay-key', currentRoleplayValue);

                const activatedRoleplayUrl = resolveRoleplayBrowserUrl(window.location.href, {
                    isActive: true,
                    roleplay_session_mode: response?.data?.roleplay_session_mode || 'embedded',
                });

                window.history.replaceState({}, '', activatedRoleplayUrl);

                const activationEvaluation = {
                    ...(response?.data || {}),
                    roleplay_scenario: response?.data?.roleplay_scenario_details || {},
                };

                document.dispatchEvent(new CustomEvent('growtypeChatMessageResponseEvent', {
                    detail: {
                        response: {
                            data: {
                                question_evaluation: activationEvaluation
                            }
                        }
                    }
                }));

                switchRoleplayConversationSession($targetChatForm, {
                    sessionToken: response?.data?.session_token || '',
                    roleplayScenario: currentRoleplayValue,
                    traceId: response?.data?.roleplay_trace_id || '',
                    renderResponse: response,
                    activatedBackgroundValue: response?.data?.roleplay_background_value || '',
                    isActive: true,
                    roleplayKey: currentRoleplayValue,
                    onRendered: function () {
                        if (typeof window.syncActiveChatSession === 'function') {
                            window.syncActiveChatSession();
                        }
                        $targetButton.data('roleplayStartLoading', false);
                        $targetButton.data('roleplayExitLoading', false);
                    },
                    onError: function (refreshError) {
                        restoreRoleplayTransitionMessages($targetChatForm);
                        console.error('[roleplay-preview-start] refresh failed after session switch', refreshError);
                        revertOptimisticRoleplayStart($targetButton, $targetChatForm);
                    },
                });
            });
        },
        onError: function (response) {
            restoreRoleplayTransitionMessages($targetChatForm);
            console.error('[roleplay-preview-start] failed', response);
            logRoleplayStep('[roleplay-preview-start] onError', {
                status: response?.status || null,
            }, $targetButton, $targetChatForm);
            revertOptimisticRoleplayStart($targetButton, $targetChatForm);
        }
    });

    return true;
}

export function handleRoleplayExitClick(event) {
    event.preventDefault();

    const $button = $(event.currentTarget);
    const $settingsModal = $('#growtypeChatSettingsModal');
    const clickedFromInsideModal = $settingsModal.length && $settingsModal[0].contains(event.currentTarget);
    const $chatForm = $button.closest('.g-chatform').length
        ? $button.closest('.g-chatform')
        : $('.g-chatform').first();
    const $roleplayWrapper = $button.closest('.roleplay-details-wrapper');
    const nextUrl = buildNormalChatUrl(window.location.href);
    const $roleplaySelect = $('select[name="roleplay_scenario"]').first();
    const $backgroundSelect = $('select[name="chat_background_value"]').first();
    const selectedRoleplayValue = ($roleplaySelect.val() || '').trim();
    const currentRoleplayValue = selectedRoleplayValue !== '' && selectedRoleplayValue !== 'none'
        ? selectedRoleplayValue
        : ($button.attr('data-roleplay-key') || '').trim();
    const requestState = getRoleplayRequestState($chatForm);
    const previousRoleplayValue = $roleplaySelect.val() || '';
    const previousBackgroundValue = $backgroundSelect.val() || '';
    const baseBackgroundValue = getBaseBackgroundValue();
    logRoleplayStep('[roleplay-toggle] click:entry', {
        clickedFromInsideModal,
        nextUrl,
        requestState,
        selectedRoleplayValue,
        previousRoleplayValue,
        previousBackgroundValue,
        baseBackgroundValue,
    }, $button, $chatForm);

    if (requestState !== ROLEPLAY_REQUEST_STATES.IDLE) {
        logRoleplayStep('[roleplay-toggle] click ignored while processing', {
            clickedFromInsideModal,
            requestState,
        }, $button, $chatForm);
        return;
    }

    const applyOptimisticRoleplaySelectOff = function () {
        if (!$roleplaySelect.length) return;

        // Intentionally left as a no-op: the scenario select must keep
        // whatever value the user had picked while roleplay is toggled off,
        // so it doesn't need to be reset to 'none' and no 'change' event
        // should fire (that event is what drives handleRoleplayChange,
        // which would otherwise flip data-roleplay back to '1').
        chatDebugLog('roleplayStep', '[roleplay-stop] roleplay select left unchanged', {
            roleplayValue: $roleplaySelect.val() || '',
            chatFormRoleplay: $chatForm.attr('data-roleplay'),
        });
    };

    const restorePreviousRoleplaySelect = function () {
        if (!$roleplaySelect.length) return;

        chatDebugLog('roleplayStep', '[roleplay-stop] roleplay select unchanged (restore skipped)', {
            roleplayValue: $roleplaySelect.val() || '',
            chatFormRoleplay: $chatForm.attr('data-roleplay'),
        });
    };

    const optimisticStart = false;
    $button.removeData('roleplayStartIntent');
    const isRoleplayActive = optimisticStart
        ? false
        : $chatForm.attr('data-roleplay') === '1';

    chatDebugLog('roleplayStep', '[roleplay-toggle] click', {
        sessionToken: getSessionToken($chatForm),
        roleplayKey: currentRoleplayValue,
        buttonActive: $button.attr('data-roleplay-active'),
        chatFormRoleplay: $chatForm.attr('data-roleplay'),
        sessionBound: $roleplayWrapper.attr('data-roleplay-session-bound'),
        requestState,
        isRoleplayActive,
        optimisticStart,
    });

    if (!isRoleplayActive) {
        startRoleplayInCurrentChat({
            $button,
            $chatForm: $chatForm,
            roleplayScenario: currentRoleplayValue,
            $modalToClose: !clickedFromInsideModal ? $settingsModal : $(),
        });
        return;
    }

    clearRoleplayStoryStarted(getSessionToken($chatForm));

    const sessionToken = getSessionToken($chatForm);
    const stopBlockedByMissingSession = !sessionToken;
    const stopBlockedByLoading = requestState !== ROLEPLAY_REQUEST_STATES.IDLE;
    if (stopBlockedByMissingSession || stopBlockedByLoading) {
        chatDebugLog('roleplayStep', '[roleplay-toggle] stop blocked', {
            sessionToken,
            roleplayKey: currentRoleplayValue,
            missingSessionToken: stopBlockedByMissingSession,
            blockedByExitLoading: stopBlockedByLoading,
            buttonActive: $button.attr('data-roleplay-active'),
            chatFormRoleplay: $chatForm.attr('data-roleplay'),
            sessionBound: $roleplayWrapper.attr('data-roleplay-session-bound'),
        });
        return;
    }

    beginOptimisticRoleplayStop($button, $chatForm, nextUrl);
    applyOptimisticRoleplaySelectOff();
    logRoleplayStep('[roleplay-stop] optimistic stop applied before modal close', {
        clickedFromInsideModal,
        nextUrl,
    }, $button, $chatForm);

    if (!clickedFromInsideModal) {
        logRoleplayStep('[roleplay-stop] modal close before request', {
            clickedFromInsideModal,
        }, $button, $chatForm);
        forceCloseModal($settingsModal);
    }

    let restoredBackgroundValue = baseBackgroundValue || $backgroundSelect.val() || '';

    chatDebugLog('roleplayStep', '[roleplay-stop] request queued', {
        sessionToken,
        roleplayScenario: currentRoleplayValue,
        backgroundValue: restoredBackgroundValue,
    });

    requestRoleplayStop({
        chatForm: $chatForm,
        submitBtn: $button,
        roleplayScenario: currentRoleplayValue,
        backgroundValue: restoredBackgroundValue,
        onSuccess: function (response) {
            logRoleplayStep('[roleplay-stop] onSuccess:entered', {
                responseDataKeys: Object.keys(response?.data || {}),
            }, $button, $chatForm);
            syncResponseCharacterIdentity($chatForm, response?.data?.response_character);

            if ($roleplaySelect.length) {
                applyOptimisticRoleplaySelectOff();
                restoredBackgroundValue = restoreBaseBackgroundState({ forceApply: true }) || restoredBackgroundValue;
            }

            $chatForm.attr('data-roleplay-key', '');

            completeRoleplayStopUi($button, $chatForm, nextUrl);

            const finalizeStopUi = function () {
                logRoleplayStep('[roleplay-stop] finalize:start', {
                    restoredBackgroundValue,
                }, $button, $chatForm);

                switchRoleplayConversationSession($chatForm, {
                    sessionToken: response?.data?.session_token || '',
                    roleplayScenario: '',
                    traceId: response?.data?.roleplay_trace_id || '',
                    renderResponse: response,
                    activatedBackgroundValue: restoredBackgroundValue,
                    isActive: false,
                    roleplayKey: '',
                    onRendered: function () {
                        if (typeof window.syncActiveChatSession === 'function') {
                            window.syncActiveChatSession();
                        }
                        $button.data('roleplayExitLoading', false);
                        applyRoleplayUiState($chatForm, {
                            isActive: false,
                            requestState: ROLEPLAY_REQUEST_STATES.IDLE,
                        });
                        scrollToChatBottom(0, true);
                        window.requestAnimationFrame(function () {
                            scrollToChatBottom(0, true);
                        });
                        logRoleplayStep('[roleplay-stop] finalize:complete', {
                            restoredBackgroundValue,
                            nextSessionToken: response?.data?.session_token || '',
                        }, $button, $chatForm);
                    },
                    onError: function (refreshError) {
                        console.error('[roleplay-stop] refresh failed after session switch', refreshError);
                        restorePreviousRoleplaySelect();
                        revertOptimisticRoleplayStop($button, $chatForm, currentRoleplayValue);
                    },
                });
            };

            logRoleplayStep('[roleplay-stop] finalize immediately on success', {}, $button, $chatForm);
            finalizeStopUi();
        },
        onError: function (response) {
            restorePreviousRoleplaySelect();

            if ($backgroundSelect.length) {
                $backgroundSelect.val(previousBackgroundValue);
                if ($.fn.chosen) {
                    $backgroundSelect.trigger('chosen:updated');
                }
                $backgroundSelect.trigger('change');
            }

            console.error('[roleplay-exit] failed', response);
            logRoleplayStep('[roleplay-stop] onError', {
                status: response?.status || null,
                responseText: response?.responseText || '',
            }, $button, $chatForm);
            revertOptimisticRoleplayStop($button, $chatForm, currentRoleplayValue);
        }
    });
}

export function initRoleplayExitActions() {
    const toggleSelector = '[data-roleplay-toggle="true"], [data-roleplay-settings-toggle="true"], [data-roleplay-exit="true"], .roleplay-details-toggle';
    const buttonSelector = '[data-roleplay-toggle="true"], [data-roleplay-settings-toggle="true"], .roleplay-details-toggle';

    $(document)
        .off('pointerdown.roleplayExit', buttonSelector)
        .on('pointerdown.roleplayExit', buttonSelector, function () {
            const $button = $(this);
            const $chatForm = $button.closest('.g-chatform').length
                ? $button.closest('.g-chatform')
                : $('.g-chatform').first();
            const $roleplayWrapper = $button.closest('.roleplay-details-wrapper');
            const requestState = getRoleplayRequestState($chatForm);
            const isActive = $chatForm.attr('data-roleplay') === '1';

            chatDebugLog('roleplayStep', '[roleplay-toggle] pointerdown', {
                sessionToken: getSessionToken($chatForm),
                roleplayKey: ($button.attr('data-roleplay-key') || '').trim(),
                buttonActive: $button.attr('data-roleplay-active'),
                chatFormRoleplay: $chatForm.attr('data-roleplay'),
                sessionBound: $roleplayWrapper.attr('data-roleplay-session-bound'),
                requestState,
                isLoading: $button.hasClass('is-loading'),
                startLoading: $button.data('roleplayStartLoading') === true,
                exitLoading: $button.data('roleplayExitLoading') === true,
                isActive,
            });

        });

    $(document)
        .off('click.roleplayExit', toggleSelector)
        .on('click.roleplayExit', toggleSelector, handleRoleplayExitClick);
}

export function initRoleplayHeaderObserver() {
    logRoleplayStep('[roleplay-header] observer:init:start');
    ensureAllRoleplayToggleButtons($(document));

    if (window.MutationObserver) {
        const headerObserver = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
                mutation.addedNodes.forEach(function (node) {
                    if (node.nodeType !== 1) return;

                    const $added = $(node);

                    if ($added.is('.g-chatform-header')) {
                        logRoleplayStep('[roleplay-header] observer:header-added', {}, $added.find('[data-roleplay-toggle="true"], [data-roleplay-settings-toggle="true"]').first(), $added.closest('.g-chatform'));
                        ensureRoleplayToggleButton(getCanonicalRoleplayHeaderWrapper($added));
                        syncRoleplayStateWithHeader($added.closest('.g-chatform'));
                    } else if ($added.find('.g-chatform-header').length) {
                        $added.find('.g-chatform-header').each(function () {
                            logRoleplayStep('[roleplay-header] observer:nested-header-added', {}, $(this).find('[data-roleplay-toggle="true"], [data-roleplay-settings-toggle="true"]').first(), $(this).closest('.g-chatform'));
                            ensureRoleplayToggleButton(getCanonicalRoleplayHeaderWrapper($(this)));
                            syncRoleplayStateWithHeader($(this).closest('.g-chatform'));
                        });
                    }
                });
            });
        });

        const target = document.querySelector('.g-chatform') || document.body;
        if (target) {
            headerObserver.observe(target, {
                childList: true,
                subtree: true,
            });
        }
    }

    const header = document.querySelector('.g-chatform-header');
    if (!header || header.dataset.roleplayCloseObserverInitialized === 'true') {
        logRoleplayStep('[roleplay-header] observer:init:skipped', {
            hasHeader: !!header,
            alreadyInitialized: header?.dataset?.roleplayCloseObserverInitialized === 'true',
        });
        return;
    }

    header.dataset.roleplayCloseObserverInitialized = 'true';
    ensureRoleplayToggleButton(getCanonicalRoleplayHeaderWrapper($(header)));
    syncRoleplayStateWithHeader($(header).closest('.g-chatform'));
    logRoleplayStep('[roleplay-header] observer:init:complete');
}

function initRoleplayStateChangeListener() {
    document.addEventListener('growtypeChatRoleplayStateChanged', function (event) {
        const detail = event.detail || {};
        const roleplayKey = detail.roleplayKey || '';
        const shouldRefresh = detail.refreshChatContent === true;

        chatDebugLog('roleplayStep', '[roleplay-trace] growtypeChatRoleplayStateChanged event received', {
            detail,
            roleplayKey,
            shouldRefresh,
        });

        if (!shouldRefresh) return;

        const $chatForm = $('.g-chatform').first();
        if (!$chatForm.length) return;

        clearGuestLocalHistory($chatForm);

        const overrides = {
            roleplay_scenario: roleplayKey !== 'none' ? roleplayKey : '',
        };

        const isRoleplayActive = Boolean(roleplayKey && roleplayKey !== 'none');
        const backgroundController = getBackgroundStateController();
        let restoredBackgroundValue = '';

        if (!isRoleplayActive && backgroundController) {
            if (typeof backgroundController.restoreBaseBackgroundState === 'function') {
                restoredBackgroundValue = backgroundController.restoreBaseBackgroundState({
                    forceApply: true,
                }) || '';
            }

            if (!restoredBackgroundValue && typeof backgroundController.getBaseBackgroundValue === 'function') {
                restoredBackgroundValue = backgroundController.getBaseBackgroundValue() || '';
            }
        }

        refreshRoleplayStoryContent($chatForm, overrides, null, null, {
            animateFreshMessages: isRoleplayActive,
            restoreGuestHistory: false,
            clearGuestHistoryFirst: true,
            activatedBackgroundValue: restoredBackgroundValue,
            scrollToBottom: true,
        });
    });
}

export function roleplayToggle() {
    initRoleplayStartStoryActions();
    initRoleplayExitActions();
    initRoleplayHeaderObserver();
    initRoleplayStateChangeListener();
}
